/**
 * Service worker registration for the GDB SPA (offline-after-first-load).
 *
 * Extracted from `main.tsx` so the registration guard is unit-testable without
 * relying on Vite's static `import.meta.env.PROD` at call sites. The worker
 * itself lives at `public/sw.js` (served at `/sw.js`, scope = origin root).
 *
 * Registration is gated on production builds only — registering in dev would
 * fight Vite's HMR. The PROD value is read once via the `isProd` default so the
 * caller can override it (the tests do) without touching `import.meta`.
 */

export interface RegisterServiceWorkerOptions {
  /** Whether this is a production build. Defaults to Vite's `import.meta.env.PROD`. */
  isProd?: boolean
  /** Navigator to register against. Defaults to the global `navigator`. */
  nav?: Navigator
  /** Window to attach the `load` listener to. Defaults to the global `window`. */
  win?: Window
  /** Path the worker is served from. Defaults to `/sw.js`. */
  swUrl?: string
}

/**
 * Register the service worker after the page `load` event, in production only.
 *
 * No-ops (and returns `false`) when not a production build, when the
 * environment has no `window`, or when `serviceWorker` is unsupported. Returns
 * `true` once the `load` listener has been attached.
 */
export function registerServiceWorker(
  options: RegisterServiceWorkerOptions = {},
): boolean {
  const {
    isProd = import.meta.env.PROD,
    nav = typeof navigator !== 'undefined' ? navigator : undefined,
    win = typeof window !== 'undefined' ? window : undefined,
    swUrl = '/sw.js',
  } = options

  if (!isProd) return false
  if (!win) return false
  if (!nav || !('serviceWorker' in nav)) return false

  win.addEventListener('load', () => {
    nav.serviceWorker.register(swUrl).catch((err: unknown) => {
      console.warn('[GDB] service worker registration failed:', err)
    })
  })

  return true
}
